import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactCTA from '../components/ContactCTA';

const HealthAdvisory = () => {
  const services = [
    {
      title: 'Health Policy & Regulatory Analysis',
      description: 'Comprehensive policy analysis, legislative guidance, and strategic advisory services.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      title: 'Medicaid & Managed Care',
      description: 'Policy analysis and recommendations, program implementation support, and quality improvement projects.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
    },
    {
      title: 'Medicaid Enterprise Systems & Health IT',
      description: 'Technical assistance for system development, strategic advisory on IT requirements, and support for certification processes.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'Life Sciences & Reimbursement',
      description: 'Support for payers and manufacturers, reimbursement pathway analysis, and pricing and market access strategies.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
    },
    {
      title: 'Maternal Child Health',
      description: 'Policy analysis, strategies to improve maternal and child health outcomes, and care model design.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
    },
    {
      title: 'Federal Contracts',
      description: 'Contract acquisition expertise, project management, and tailored consulting services.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];

  const approach = [
    { step: '1', title: 'Assess and Align', description: 'We begin by understanding your challenges, goals, and priorities to ensure our approach aligns with your vision.' },
    { step: '2', title: 'Design and Develop', description: 'We create tailored strategies and solutions that address your specific needs and objectives.' },
    { step: '3', title: 'Implement and Evaluate', description: 'We work alongside you to implement solutions and measure their impact on your organization.' },
    { step: '4', title: 'Adapt and Improve', description: 'We continuously refine our approach based on results and feedback to ensure sustained success.' },
  ];

  const clients = [
    { name: 'Federal and State Government Agencies', description: 'Developing, implementing, or evaluating health and human services programs.' },
    { name: 'Managed Care Organizations', description: 'Strengthening market strategy, improving product offering, and enhancing compliance with regulatory requirements.' },
    { name: 'Hospitals and Health Systems', description: 'Aligning operations, quality initiatives, and population health strategies.' },
    { name: 'Life Science Companies', description: 'Developing innovative therapies, diagnostics, and digital health solutions that intersect with public programs, regulatory frameworks, and healthcare delivery systems.' },
    { name: 'Nonprofits and Community-Based Organizations', description: 'Advancing public health initiatives and safety net programs through policy and partnership support.' },
  ];

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section className="hero-section bg-gradient-to-br from-gray-900 via-onyx-900 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white mb-9">
              Trusted Advisors In Government Programs and Health Policy
            </h1>
            <p className="text-2xl italic text-white max-w-3xl mx-auto">
              Advancing healthcare through policy insights and innovation.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <img src="/onyx-rose-advisors-icononly.png" alt="Onyx Rose Advisors Logo" className="w-48 h-48" />
              </div>
              <div className="hidden md:block w-px self-stretch bg-gray-300 flex-shrink-0"></div>
              <div className="flex-1">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  About Onyx Health Advisory
                </h2>
                <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
                   At Onyx Rose Advisors, our team's expertise spans health policy, program design, and healthcare strategy. We partner with government agencies, managed care organizations, hospitals, and nonprofits to translate complex policy goals into actionable plans that improve performance and strengthen systems. From program strategy and regulatory analysis to operational planning and partnership development, we deliver tailored solutions that help clients navigate change, optimize outcomes, and achieve measurable, lasting impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Areas of Expertise
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive services across multiple domains of healthcare policy and operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Approach
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a collaborative, results-driven methodology to ensure success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {approach.map((item, index) => (
              <div
                key={index}
                className="text-center"
              >
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 bg-accent-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-white text-4xl font-bold">Who We Serve</h2>
              <p className="mt-3 text-white/80 max-w-3xl">
                  We partner across the ecosystem. Browse the segments below.
              </p>

              <div className="relative mt-10">
                  {/* Left Arrow */}
                  <button
                      onClick={() => {
                          const container = document.getElementById('clients-carousel');
                          container.scrollBy({ left: -420, behavior: 'smooth' });
                      }}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                      aria-label="Scroll left"
                  >
                      <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                  </button>

                  {/* Right Arrow */}
                  <button
                      onClick={() => {
                          const container = document.getElementById('clients-carousel');
                          container.scrollBy({ left: 420, behavior: 'smooth' });
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                      aria-label="Scroll right"
                  >
                      <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                  </button>

                  <div id="clients-carousel" className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {clients.map((c) => (
                          <div key={c.name} className="snap-start shrink-0 w-[400px] rounded-2xl bg-white p-6 shadow-md flex gap-4">
                              <p className="text-lg font-semibold text-gray-900 w-2/5">{c.name}</p>
                              <div className="w-px bg-gray-300 flex-shrink-0"></div>
                              <p className="text-md font-semibold text-gray-600 w-3/5">{c.description}</p>
                          </div>
                      ))}
                  </div>
              </div>
          </div>
      </section>
      {/* Value Proposition Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8">
              Why Choose Onyx Health Advisory
            </h2>
            <div className="flex flex-col md:flex-row gap-12 mt-12 md:divide-x md:divide-gray-300">
              <div className="text-center md:px-8 flex-1">
                <h3 className="text-2xl font-bold mb-4">Policy Expertise with Practical Execution</h3>
                <p className="text-gray-700 leading-relaxed">
                  We combine deep policy knowledge with hands-on implementation experience.
                </p>
              </div>
              <div className="text-center md:px-8 flex-1">
                <h3 className="text-2xl font-bold mb-4">Collaboration-Driven Approach</h3>
                <p className="text-gray-700 leading-relaxed">
                  We work alongside your team as trusted partners, not just consultants.
                </p>
              </div>
              <div className="text-center md:px-8 flex-1">
                <h3 className="text-2xl font-bold mb-4">Actionable Insights</h3>
                <p className="text-gray-700 leading-relaxed">
                  We deliver strategies that translate complex policy into practical solutions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA
        title="Ready to Partner With Us?"
        description="Contact us today to learn how Onyx Health Advisory can help your organization navigate complex healthcare challenges and drive meaningful impact."
      />

      <Footer />
    </div>
  );
};

export default HealthAdvisory;
